import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../components/Breadcrumb";
import CardLists from "../components/CardLists";
import DecksTable from "../components/DecksTable";
import Description from "../components/Description";
import Error from "../components/Error";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import Mana from "../components/Mana";
import Panels from "../components/Panels";
import RelatedInfo from "../components/RelatedInfo";
import SubNav from "../components/SubNav";
import classNames from "classnames";
import { fetchPageData } from "../store/pagedata";
import styles from "../styles/default.module.scss";
import useDarkMode from "use-dark-mode";
import { useParams } from "react-router-dom";

const Default = () => {
  const { dir, page, subpage } = useParams();
  const darkMode = useDarkMode(false);
  const dispatch = useDispatch();
  const { data, error, isLoading } = useSelector((state) => state.pagedata);
  const { pathname, search } = useSelector((state) => state.router.location);

  useEffect(async () => {
    dispatch(fetchPageData());
  }, [pathname, search]);

  if (error) {
    return (
      <Layout>
        <Helmet>
          <title>{error ? "Error" : "Loading..."} | EDHREC</title>
        </Helmet>
        <Error error={error} />
      </Layout>
    );
  }

  const alternateTitles = {
    sets: "All Sets",
    themes: "All Themes",
    tribes: "All Tribes",
  };
  const title = data?.container?.title || alternateTitles[dir];

  console.log(data);

  return (
    <Layout>
      <Helmet>
        <meta
          name="description"
          content="EDH Recommendations and strategy content for Magic: the Gathering Commander"
        />
        {data?.container && (
          <meta
            name="keywords"
            content={`${data.container.keywords}${
              data.container.keywords && ","
            }EDHREC,EDH,recommendations,Commander,MTG,Magic,staples,strategy,content`}
          />
        )}
        <title>{title ? `${title} | EDHREC` : "EDHREC"}</title>
      </Helmet>
      <div className={classNames(styles.container, { [styles.dark]: darkMode.value })}>
        <Breadcrumb breadcrumb={data?.breadcrumb || data?.container?.breadcrumb} />
        <>
          <div className={styles.headingContainer}>
            <h4 className="page-heading">{title}</h4>
          </div>
          {dir === "combos" && !page ? (
            <>
              <Description>{data?.description}</Description>
              <div className={styles.relatedInfoContainer}>
                {data?.related_info?.map(({ header, items }) => (
                  <SubNav
                    items={items.map(({ colors, count, textLeft, url }) => {
                      return {
                        image: <Mana colors={colors} />,
                        padLeft: true,
                        textLeft,
                        textRight: `${count} decks`,
                        url,
                      };
                    })}
                    key={header}
                  />
                ))}
              </div>
            </>
          ) : dir === "decks" ? (
            <>
              <Panels />
              <DecksTable rows={data?.table} />
            </>
          ) : dir === "sets" && !page && data?.data ? (
            <>
              <Description>
                These are the most popular cards from each set. Click a card below for more cards
                from that set.
              </Description>
              <SubNav
                items={data.data.map(({ code, icon_svg_uri, name }) => ({
                  image: <img alt={code} className={styles.setIcon} src={icon_svg_uri} />,
                  textLeft: name,
                  url: `/sets/${code}/`,
                }))}
              />
            </>
          ) : ["themes_DISABLED", "tribes_DISABLED"].includes(dir) && !page ? (
            <>
              {dir === "tribes" ? (
                <Description>
                  Decks are considered tribal if they have more than 10 creatures and at least 60.0%
                  of them share a tribe.
                </Description>
              ) : dir === "themes" ? (
                <Description>
                  These are various deck building themes and archetypes. Click a card below for more
                  info on that theme.
                </Description>
              ) : null}
              <SubNav
                items={
                  data?.container.json_dict?.cardlists &&
                  data.container.json_dict.cardlists[0].cardviews
                    .filter(({ dummy }) => !dummy)
                    .map(({ image_uris, label, url, name }) => {
                      const count = parseInt(label).toString();
                      const page = label.slice([count.length + 1], label.length - 6);
                      return {
                        image: (
                          <img
                            alt={name}
                            className={styles.art}
                            src={`https://c1.scryfall.com/file/scryfall-cards/normal/front/${image_uris[0]}`}
                          />
                        ),
                        textLeft: page,
                        textRight: `${count} decks`,
                        url: url,
                      };
                    })
                }
              />
            </>
          ) : /^\/(commanders|top)\/?(month|salt|salt-2019|week|year)?\/?$/.test(pathname) ? (
            <>
              {data?.description && <Description>{data.description}</Description>}
              {!pathname.includes("salt") && (
                <SubNav
                  items={[
                    {
                      textLeft: "Past Week",
                      url: `/${dir}/week`,
                    },
                    {
                      textLeft: "Past Month",
                      url: `/${dir}/month`,
                    },
                    {
                      textLeft: "Past 2 Years",
                      url: `/${dir}/year`,
                    },
                  ]}
                />
              )}
              {data?.container && <CardLists />}
            </>
          ) : (
            <>
              <RelatedInfo {...data?.relatedinfo} />
              {((["average-decks", "cards", "commanders", "deckpreview", "decks"].includes(dir) &&
                ![
                  "w",
                  "u",
                  "b",
                  "r",
                  "g",
                  "wu",
                  "ub",
                  "br",
                  "rg",
                  "gw",
                  "wb",
                  "ur",
                  "bg",
                  "rw",
                  "gu",
                  "wub",
                  "ubr",
                  "brg",
                  "rgw",
                  "gwu",
                  "wbg",
                  "urw",
                  "bgu",
                  "rwb",
                  "gur",
                  "wubr",
                  "ubrg",
                  "brgw",
                  "rgwu",
                  "gwub",
                  "wubrg",
                  "colorless",
                ].includes(page)) ||
                (dir === "combos" && subpage)) && <Panels />}
              {(!isLoading || ["average-decks"].includes(dir)) && (
                <Description>{data?.description}</Description>
              )}
              {data?.container && dir !== "average-decks" && <CardLists />}
            </>
          )}
        </>
      </div>
    </Layout>
  );
};

export default Default;
